import { useEffect, useState } from "react";

/**
 * Tracks which section the reader is currently in.
 *
 * Uses a scroll listener rather than IntersectionObserver because the rule is
 * positional — "the last section whose top has passed the reading line" — which
 * stays stable for sections both taller and shorter than the viewport.
 */
export function useActiveSection(ids: readonly string[]): string {
  const [active, setActive] = useState(ids[0] ?? "");

  useEffect(() => {
    let frame = 0;

    const measure = () => {
      frame = 0;
      // The reading line sits a third of the way down the viewport.
      const readingLine = window.scrollY + window.innerHeight / 3;
      let current = ids[0] ?? "";

      for (const id of ids) {
        const element = document.getElementById(id);
        if (!element) continue;
        if (element.offsetTop <= readingLine) current = id;
      }

      // The last section can be too short to ever reach the reading line.
      const atBottom =
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 24;
      setActive(atBottom ? (ids.at(-1) ?? current) : current);
    };

    const onScroll = () => {
      if (frame === 0) frame = window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, [ids]);

  return active;
}
